import React from "react";
import { Helmet } from "react-helmet-async";
import { COMPANY } from "@/data/company";

/**
 * schema.org/LocalBusiness JSON-LD for a town coverage page.
 * Represents Lutrom's service coverage for a specific town — with the real
 * Northwich HQ address as the physical location and the town as areaServed.
 * Uses a distinct per-town @id so it does not conflict with the site-wide
 * LocalBusiness schema emitted by StructuredData.jsx.
 *
 * Deliberately omits:
 *  - aggregateRating / review (no fake reviews)
 *  - priceRange (no fake pricing on per-town records)
 *  - openingHoursSpecification (deferred to the master #business record)
 *  - geo coordinates (no fake geo for a service area)
 */
const TownLocalBusinessSchema = ({ townSlug, townName, regionName }) => {
  const url = `https://www.lutrom.co.uk/coverage/${townSlug}`;

  const data = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${url}#business`,
    name: `${COMPANY.name} — ${townName} coverage`,
    description: `Industrial and commercial engineering coverage in ${townName}${regionName ? ` (${regionName})` : ""}. Mechanical, electrical, controls and compliance work delivered from Lutrom's Northwich workshop.`,
    url,
    telephone: COMPANY.phone.display,
    email: COMPANY.email.display,
    image: "https://www.lutrom.co.uk/gallery/lutrom-machines.jpg",
    logo: "https://www.lutrom.co.uk/brand/lutrom-logo-white.png",
    address: {
      "@type": "PostalAddress",
      streetAddress: `${COMPANY.address.line1}, ${COMPANY.address.line2}`,
      addressLocality: COMPANY.address.city,
      postalCode: COMPANY.address.postcode,
      addressCountry: "GB",
    },
    areaServed: {
      "@type": "AdministrativeArea",
      name: townName,
      ...(regionName ? { containedInPlace: { "@type": "AdministrativeArea", name: regionName } } : {}),
    },
    parentOrganization: { "@id": "https://www.lutrom.co.uk/#business" },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

export default TownLocalBusinessSchema;
